import { Injectable } from '@angular/core';
import { GatewayAPIService } from '../../core/gateway-api.service';
import { MerchantGroup } from '../merchant/merchantGroup';
import { FeatureFlag } from './featureFlag';
import { NavMenuItem } from '../ui/navMenuItem';
import { MerchantBrand } from '../../components/admin/merchant-brands/merchant-brands.component';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  options = { authenticate: true, setRoot: true };

  constructor(private api: GatewayAPIService) {}

  public createBrand(brand: string): Promise<void> {
    return this._post({ name: brand }, '/admin/brands');
  }

  public createGroup(group: string): Promise<void> {
    return this._post({ name: group }, '/admin/groups');
  }

  public updateBrand(oldName: string, newName: string): Promise<void> {
    return this._put({ oldName, newName }, '/admin/brands');
  }

  public updateGroup(oldName: string, newName: string): Promise<void> {
    return this._put({ oldName, newName }, '/admin/groups');
  }

  public deleteBrand(brand: string): Promise<void> {
    return this._delete('/admin/brands', { name: brand });
  }

  public deleteGroup(group: string): Promise<void> {
    return this._delete('/admin/groups', { name: group });
  }

  public getMerchantsForBrand(name: string): Promise<any[]> {
    return this._get('/admin/brands/merchants', { name: name });
  }

  public getMerchantsForGroup(name: string): Promise<any[]> {
    return this._get('/admin/groups/merchants', { name: name });
  }

  public getMerchantGroups(): Promise<MerchantGroup[]> {
    return this._get('/admin/groups', null).then(
      (response: Partial<MerchantGroup>[]) =>
        response.map((s) => new MerchantGroup(s)),
    );
  }

  public getMerchantBrands(): Promise<string[]> {
    return this._get('/admin/brands', null);
  }

  public getMerchantBrandsUsed(): Promise<MerchantBrand[]> {
    return this._get('/admin/brands/used', null);
  }

  private _get(endpoint: string, data: any): Promise<any> {
    return this.api.apiCallSane(
      endpoint,
      'GET',
      this.api.getDefaultHeaders(),
      data,
      null,
      this.options,
    );
  }

  private _post(record: any, action: string): Promise<void> {
    return this.api.apiCallSane(
      action,
      'POST',
      this.api.getDefaultHeaders(),
      null,
      record,
      this.options,
    );
  }

  private _put(record: any, action: string): Promise<void> {
    return this.api.apiCallSane(
      action,
      'PUT',
      this.api.getDefaultHeaders(),
      null,
      record,
      this.options,
    );
  }

  private _delete(action: string, data: any): Promise<void> {
    return this.api.apiCallSane(
      action,
      'DELETE',
      this.api.getDefaultHeaders(),
      null,
      data,
      this.options,
    );
  }

  public getFeatureFlags(): Promise<FeatureFlag[]> {
    return this._get('/admin/feature-flags', null);
  }

  public createFeatureFlag(name: string): Promise<FeatureFlag[]> {
    return this._post({ name: name }, '/admin/feature-flags').then(
      (response: any) => response.map((s: any) => new FeatureFlag(s)),
    );
  }

  public updateFeatureFlag(
    id: string,
    name: string,
    enabled: boolean,
  ): Promise<FeatureFlag[]> {
    return this._put(
      { id: id, name: name, enabled: enabled },
      '/admin/feature-flags',
    ).then((response: any) => response.map((s: any) => new FeatureFlag(s)));
  }

  public deleteFeatureFlag(id: string): Promise<FeatureFlag[]> {
    return this._delete('/admin/feature-flags', { id: id }).then(
      (response: any) => response.map((s: any) => new FeatureFlag(s)),
    );
  }

  public getFeatureFlagByName(name: string): Promise<boolean> {
    return this._get('/admin/feature-flag', { name: name }).then(
      (response: any) => response as boolean,
    );
  }

  public getNavigation(): Promise<NavMenuItem[]> {
    return this._get('/admin/nav', null).then((response: any) => response.map((s: any) => new NavMenuItem(s)));
  }
}
